import { BattleState, DistanceType, Monster, StanceType } from '../types';
import { AIConfig } from './types';
import { getSpecialReserveMultiplier, getReflectorReserveMultiplier } from './resourceOptimizer';

/**
 * Lv6 達人 AI のゲーム木探索における葉ノード評価関数
 *
 * 局面の優劣を線形和スコアで数値化する。
 * α-β 探索の最深部で呼ばれ、自分にとって有利な状態ほど大きい値を返す。
 *
 * 評価軸（線形和）:
 * - HP差比率: 圧倒的に支配的な軸
 * - 距離有利度: 自分の戦法（近距離志向 vs 遠距離志向）に応じた距離評価
 * - 特殊攻撃残数差: 終盤の火力資源
 * - リフレクター残数差: 守備資源（reflectRate で重み調整）
 * - スタンス相性: わずかな加点
 * - KO ボーナス: 勝敗が確定した状態で巨大な値
 *
 * 注意: 係数は仮置き。勝率測定で grid search してチューニング予定。
 */

/** 評価関数の係数（テストで上書き可能なよう export） */
export const POSITION_WEIGHTS = {
  /** HP差比率（自分HP割合 - 相手HP割合）に乗ずる係数 */
  HP_DIFF: 1000,
  /** 距離有利度（自分の戦法に距離がマッチ）の係数 */
  DISTANCE_ADVANTAGE: 50,
  /** 特殊攻撃残数1回あたりの価値 */
  SPECIAL_RESERVE: 30,
  /** リフレクター残数1回あたりの価値（reflectRate × 係数） */
  REFLECTOR_RESERVE: 25,
  /** スタンス相性ボーナス */
  STANCE_MATCH: 15,
  /** KO ボーナス（勝敗確定時） */
  KO_BONUS: 99999,
} as const;

/**
 * 局面を評価して数値スコアを返す
 *
 * PR4: aiConfig が渡された場合、resourceOptimization パラメータで
 * 特殊残数・リフレクター残数の評価を試合進行度に応じて動的調整する。
 *
 * @param state 現在のバトル状態
 * @param ownPlayerId 自分のプレイヤーID
 * @param ownMonster 自分のモンスター
 * @param opponentMonster 相手のモンスター
 * @param aiConfig 任意。指定された場合 resourceOptimization で動的補正
 * @returns スコア（自分有利なら正、不利なら負）
 */
export function evaluatePosition(
  state: BattleState,
  ownPlayerId: 'player1' | 'player2',
  ownMonster: Monster,
  opponentMonster: Monster,
  aiConfig?: AIConfig,
): number {
  const opponentId: 'player1' | 'player2' = ownPlayerId === 'player1' ? 'player2' : 'player1';
  const own = state[ownPlayerId];
  const opp = state[opponentId];

  // KO判定（最優先）
  if (own.currentHp <= 0) return -POSITION_WEIGHTS.KO_BONUS;
  if (opp.currentHp <= 0) return POSITION_WEIGHTS.KO_BONUS;

  let score = 0;

  // 1. HP差比率（最も支配的）
  const ownHpRatio = own.currentHp / ownMonster.stats.hp;
  const oppHpRatio = opp.currentHp / opponentMonster.stats.hp;
  score += (ownHpRatio - oppHpRatio) * POSITION_WEIGHTS.HP_DIFF;

  // 2. 距離有利度
  // 自分の戦法（strength vs special）に応じた最適距離との合致度
  const ownPrefersClose = ownMonster.stats.strength > ownMonster.stats.special;
  const oppPrefersClose = opponentMonster.stats.strength > opponentMonster.stats.special;
  const distance = state.currentDistance;

  if (ownPrefersClose && distance === DistanceType.NEAR) {
    score += POSITION_WEIGHTS.DISTANCE_ADVANTAGE;
  } else if (!ownPrefersClose && distance === DistanceType.FAR) {
    score += POSITION_WEIGHTS.DISTANCE_ADVANTAGE;
  }
  if (oppPrefersClose && distance === DistanceType.FAR) {
    score += POSITION_WEIGHTS.DISTANCE_ADVANTAGE;
  } else if (!oppPrefersClose && distance === DistanceType.NEAR) {
    score += POSITION_WEIGHTS.DISTANCE_ADVANTAGE;
  }

  // PR4: リソース最適化の動的乗数
  const resourceStrength = aiConfig ? aiConfig.resourceOptimization / 100 : 0;
  const specMult = getSpecialReserveMultiplier(ownHpRatio, oppHpRatio, resourceStrength);
  const refMult = getReflectorReserveMultiplier(ownHpRatio, oppHpRatio, resourceStrength);

  // 3. 特殊攻撃残数差（resourceOptimization で動的調整）
  const specDiff = own.remainingSpecialCount - opp.remainingSpecialCount;
  score += specDiff * POSITION_WEIGHTS.SPECIAL_RESERVE * specMult;

  // 4. リフレクター残数差（reflectRate で重み付け、resourceOptimization で動的調整）
  const ownRefRemaining = ownMonster.reflector.maxReflectCount - own.usedReflectCount;
  const oppRefRemaining = opponentMonster.reflector.maxReflectCount - opp.usedReflectCount;
  score += ownRefRemaining * ownMonster.reflector.reflectRate * POSITION_WEIGHTS.REFLECTOR_RESERVE * refMult;
  score -= oppRefRemaining * opponentMonster.reflector.reflectRate * POSITION_WEIGHTS.REFLECTOR_RESERVE * refMult;

  // 5. スタンス相性
  // 自分が攻勢で相手が守勢でない、または自分が守勢で相手が攻勢ならボーナス
  if (own.currentStance === StanceType.OFFENSIVE && opp.currentStance !== StanceType.DEFENSIVE) {
    score += POSITION_WEIGHTS.STANCE_MATCH;
  }
  if (own.currentStance === StanceType.DEFENSIVE && opp.currentStance === StanceType.OFFENSIVE) {
    score += POSITION_WEIGHTS.STANCE_MATCH;
  }

  return score;
}
